import { useState } from "react";
import { CalendarPlus, FileSignature, Mail, MapPin, MessageCircle, Pencil, Phone, Trash2, UserCheck } from "lucide-react";
import { money } from "../lib/finance";
import { PageTitle, Empty, Modal } from "./common";
import { waTarget } from "../lib/wa";
import { RESULT_COLORS, dayTitle, fullAddr, sortAppts } from "../lib/appt";

function Contacts({ l }) {
  const wa = waTarget(l.phone);
  return (
    <div className="customer-contact-row">
      {l.phone && <a className="contact-chip" href={`tel:${l.phone}`} onClick={(e) => e.stopPropagation()} data-testid={`lead-call-${l.id}`}><Phone size={11} /> {l.phone}</a>}
      {wa && <a className="contact-chip whatsapp" href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`lead-wa-${l.id}`}><MessageCircle size={11} /> WhatsApp</a>}
      {l.email && <a className="contact-chip" href={`mailto:${l.email}`} onClick={(e) => e.stopPropagation()}><Mail size={11} /> {l.email}</a>}
    </div>
  );
}

function History({ list }) {
  if (!list.length) return <p className="dim">Henüz randevu yok</p>;
  return (
    <ul className="appt-history-list">
      {list.map((h) => (
        <li key={h.id}><b>{dayTitle(h.date)} · {h.time}</b>
          <span>{h.subject || "Randevu"} · {h.mode}{h.result && <em className={`badge badge-${RESULT_COLORS[h.result]}`}>{h.result}</em>}</span>
          {h.note && <p>{h.note}</p>}</li>
      ))}
    </ul>
  );
}

function LeadQuotes({ list }) {
  if (!list.length) return <p className="dim">Henüz teklif yok</p>;
  return (
    <ul className="lead-quotes" data-testid="lead-quotes">
      {list.map((q) => <li key={q.id}><span>{q.branch || "Teklif"} · {q.company || "—"}</span><b>{money(q.premium || 0)}</b><em className="badge badge-slate">{q.status}</em></li>)}
    </ul>
  );
}

function LeadDetail({ lead: l, appts, quotes, actions, onClose }) {
  const addr = fullAddr(l);
  return (
    <Modal title={l.name} onClose={onClose}>
      <div className="lead-detail" data-testid="lead-detail">
        <span className="badge badge-amber" data-testid="lead-detail-origin">Potansiyel müşteri adayı · Kaynak: {l.origin || "Belirtilmemiş"}</span>
        <Contacts l={l} />
        {addr && <div className="ccard-address"><MapPin size={13} /> {addr}</div>}
        {l.note && <p className="ccard-note pre" data-testid="lead-detail-note">{l.note}</p>}
        <h4>Randevular ve görüşme notları</h4>
        <History list={appts} />
        <h4>Teklifler</h4>
        <LeadQuotes list={quotes} />
        <div className="modal-actions lead-actions">
          <button type="button" className="outline-button danger" onClick={() => actions.onDelete(l)} data-testid="lead-detail-delete"><Trash2 size={14} /> Sil</button>
          <button type="button" className="outline-button" onClick={() => actions.onEdit(l)} data-testid="lead-detail-edit"><Pencil size={14} /> Düzenle</button>
          <button type="button" className="outline-button" onClick={() => actions.onNewAppointment(l)} data-testid="lead-detail-appt"><CalendarPlus size={14} /> Randevu</button>
          <button type="button" className="outline-button" onClick={() => actions.onNewQuote(l)} data-testid="lead-detail-quote"><FileSignature size={14} /> Teklif oluştur</button>
          <button type="button" className="primary-button" onClick={() => actions.onConvert(l)} data-testid="lead-detail-convert"><UserCheck size={14} /> Müşteriye dönüştür</button>
        </div>
      </div>
    </Modal>
  );
}

export function Leads({ leads, appointments, quotes = [], onNew, ...actions }) {
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const low = (s) => (s || "").toLocaleLowerCase("tr");
  const shown = leads.filter((l) => low(l.name).includes(low(q)) || (l.phone || "").includes(q));
  const apptsOf = (id) => sortAppts(appointments.filter((a) => a.customerId === id)).reverse();
  const open = leads.find((l) => l.id === openId);
  return (
    <div data-testid="leads-page">
      <PageTitle eyebrow="ADAY PORTFÖY" title="Potansiyel Müşteri Adayları" description="Randevulardan eklenen adaylar; müşteriye dönüştürünce Müşteriler sayfasına aktarılır." action="Yeni Aday" onAction={onNew} actionTest="lead-new-button" />
      <div className="toolbar"><div className="inline-search"><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Aday ara: ad, telefon…" data-testid="lead-search" /></div></div>
      {shown.length === 0 ? <Empty text="Henüz potansiyel müşteri adayı yok" /> : (
        <div className="customer-grid">
          {shown.map((l) => {
            const ap = apptsOf(l.id);
            const last = ap[0];
            return (
              <article key={l.id} className="customer-card lead-card" onClick={() => setOpenId(l.id)} data-testid={`lead-card-${l.id}`}>
                <div className="customer-card-top"><span className="type-label lead">Aday{l.origin ? ` · ${l.origin}` : ""}</span><small className="dim">{ap.length} randevu</small></div>
                <h3>{l.name}</h3>
                <Contacts l={l} />
                {fullAddr(l) && <p className="lead-line"><MapPin size={11} /> {fullAddr(l)}</p>}
                {last && <p className="lead-line">Son randevu: {dayTitle(last.date)} {last.time}{last.result ? ` · ${last.result}` : ""}</p>}
                {l.note && <p className="lead-note">{l.note}</p>}
                <div className="lead-card-actions">
                <button type="button" className="outline-button small" onClick={(e) => { e.stopPropagation(); actions.onNewQuote(l); }} data-testid={`lead-quote-${l.id}`}><FileSignature size={13} /> Teklif oluştur</button>
                <button type="button" className="primary-button small lead-convert" onClick={(e) => { e.stopPropagation(); actions.onConvert(l); }} data-testid={`lead-convert-${l.id}`}><UserCheck size={13} /> Müşteriye dönüştür</button>
                </div>
              </article>
            );
          })}
        </div>
      )}
      {open && <LeadDetail lead={open} appts={apptsOf(open.id)} quotes={quotes.filter((q) => q.customerId === open.id)} actions={actions} onClose={() => setOpenId(null)} />}
    </div>
  );
}
